import React from 'react';
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import Table from '@mui/joy/Table';
import { METRICS_HINTS, INVENTORY_LOAD_HINTS } from '../../inventorySyncHints';
import { InventoryLoadSourceMetrics, ServiceMetricsSnapshot } from '../../../../models/inventorySync';
import { HintLabel, formatNumber, formatPercent } from './shared';

type MetricItem = { label: string; hint: string; value: string };

function MetricRow(props: { label: string; hint: string; value: string }) {
    return (
        <tr>
            <td><HintLabel label={props.label} hint={props.hint} level="body-sm" /></td>
            <td><Typography level="body-sm">{props.value}</Typography></td>
        </tr>
    );
}

function SummaryMetricCard(props: { label: string; hint: string; value: string; sub?: string }) {
    const { label, hint, value, sub } = props;
    return (
        <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'sm', height: '100%' }}>
            <HintLabel label={label} hint={hint} level="body-sm" />
            <Typography level="h4" sx={{ mt: 0.75 }}>{value}</Typography>
            {sub && <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>{sub}</Typography>}
        </Sheet>
    );
}

function MetricsSection(props: { title: string; rows: MetricItem[] }) {
    return (
        <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'sm', height: '100%' }}>
            <Typography level="title-sm" sx={{ mb: 1 }}>{props.title}</Typography>
            <Box sx={{ display: { xs: 'flex', sm: 'none' }, flexDirection: 'column', gap: 1 }}>
                {props.rows.map((row) => (
                    <Box key={row.label} sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 1, alignItems: 'start' }}>
                        <HintLabel label={row.label} hint={row.hint} level="body-sm" />
                        <Typography level="body-sm" textAlign="right" sx={{ whiteSpace: 'nowrap' }}>{row.value}</Typography>
                    </Box>
                ))}
            </Box>
            <Table borderAxis="bothBetween" size="sm" sx={{ display: { xs: 'none', sm: 'table' } }}>
                <tbody>
                    {props.rows.map((row) => (
                        <MetricRow key={row.label} label={row.label} hint={row.hint} value={row.value} />
                    ))}
                </tbody>
            </Table>
        </Sheet>
    );
}

function formatSourceCell(count: number, percent: number): string {
    return `${formatNumber(count)} (${formatPercent(percent)})`;
}

function InventoryLoadSourceTable(props: { data: InventoryLoadSourceMetrics }) {
    const { data } = props;
    // Источники загрузки — общий список для мобильной и десктопной разметки
    const sources = [
        {
            title: 'Public link',
            hint: 'Загрузка через public link / прокси.',
            batches: data.publicLinkBatchCount,
            batchesPercent: data.publicLinkBatchPercent,
            batchesHint: INVENTORY_LOAD_HINTS.publicLinkBatchCount,
        },
        {
            title: 'SteamApis',
            hint: 'Загрузка через steamapis.',
            batches: data.steamApisBatchCount,
            batchesPercent: data.steamApisBatchPercent,
            batchesHint: INVENTORY_LOAD_HINTS.steamApisBatchCount,
        },
        {
            title: 'Bot - Own',
            hint: 'Загрузка собственного инвентаря бота.',
            batches: data.botOwnBatchCount,
            batchesPercent: data.botOwnBatchPercent,
            batchesHint: INVENTORY_LOAD_HINTS.botOwnBatchCount,
        },
        {
            title: 'Bot - TradeLink',
            hint: 'Загрузка через бота по торговой ссылке.',
            batches: data.botTradeLinkBatchCount,
            batchesPercent: data.botTradeLinkBatchPercent,
            batchesHint: INVENTORY_LOAD_HINTS.botTradeLinkBatchCount,
        },
    ];

    return (
        <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'sm', overflow: 'auto' }}>
            <Box sx={{ mb: 1 }}>
                <HintLabel label="Загрузка по источникам" hint={INVENTORY_LOAD_HINTS.section} level="body-sm" />
            </Box>

            <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1.5 }}>
                {sources.map((source) => (
                    <Sheet key={source.title} variant="soft" sx={{ p: 1.5, borderRadius: 'sm' }}>
                        <HintLabel label={source.title} hint={source.hint} level="body-sm" />
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 1, mt: 1 }}>
                            <HintLabel label="Батчи" hint={source.batchesHint} />
                            <Typography level="body-sm" textAlign="right">{formatSourceCell(source.batches, source.batchesPercent)}</Typography>
                        </Box>
                    </Sheet>
                ))}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 1, px: 0.5 }}>
                    <HintLabel label="Всего батчей" hint={INVENTORY_LOAD_HINTS.totalBatchCount} />
                    <Typography level="body-sm" textAlign="right">{formatNumber(data.totalBatchCount)}</Typography>
                </Box>
            </Box>

            <Table borderAxis="bothBetween" size="sm" sx={{ '--TableCell-headFontWeight': 600, display: { xs: 'none', md: 'table' } }}>
                <thead>
                    <tr>
                        <th>Источник</th>
                        {sources.map((source) => (
                            <th key={source.title}>
                                <HintLabel label={source.title} hint={source.hint} level="body-sm" />
                            </th>
                        ))}
                        <th>
                            <HintLabel label="Всего" hint="Сумма по всем источникам." level="body-sm" />
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td><HintLabel label="Батчи" hint={INVENTORY_LOAD_HINTS.totalBatchCount} level="body-sm" /></td>
                        {sources.map((source) => (
                            <td key={source.title}>
                                <Typography level="body-sm">{formatSourceCell(source.batches, source.batchesPercent)}</Typography>
                            </td>
                        ))}
                        <td><Typography level="body-sm">{formatNumber(data.totalBatchCount)}</Typography></td>
                    </tr>
                </tbody>
            </Table>
        </Sheet>
    );
}

export function ServiceMetricsPanel(props: { metrics: ServiceMetricsSnapshot }) {
    const { metrics } = props;
    const load = metrics.inventoryLoadBySource;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', xl: 'repeat(4, 1fr)' }, gap: 1.5 }}>
                <SummaryMetricCard
                    label="Очередь / воркеры"
                    hint={`${METRICS_HINTS.queueDepth} ${METRICS_HINTS.activeWorkers}`}
                    value={`${formatNumber(metrics.queueDepth)} / ${formatNumber(metrics.activeWorkers)}`}
                    sub={`Лимит: ${metrics.currentConcurrencyLimit} / ${metrics.maxConcurrencyLimit}`}
                />
                <SummaryMetricCard
                    label="Успех"
                    hint={METRICS_HINTS.successPercent}
                    value={formatPercent(metrics.successPercent)}
                    sub={`${formatNumber(metrics.successCount)} из ${formatNumber(metrics.totalRequests)}`}
                />
                <SummaryMetricCard
                    label="429 от попыток"
                    hint={METRICS_HINTS.rateLimitPercent}
                    value={formatPercent(metrics.rateLimitPercent)}
                    sub={`${formatNumber(metrics.rateLimitCount)} из ${formatNumber(metrics.httpAttemptCount)} попыток`}
                />
                <SummaryMetricCard
                    label="Запросов с 429"
                    hint={METRICS_HINTS.requestsWith429Percent}
                    value={formatPercent(metrics.requestsWith429Percent)}
                    sub={formatNumber(metrics.requestsWith429Count)}
                />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, 1fr)' }, gap: 1.5 }}>
                <MetricsSection
                    title="Запросы"
                    rows={[
                        { label: 'Всего', hint: METRICS_HINTS.totalRequests, value: formatNumber(metrics.totalRequests) },
                        { label: 'Успешных', hint: METRICS_HINTS.successCount, value: formatNumber(metrics.successCount) },
                        { label: 'С ошибкой', hint: METRICS_HINTS.errorCount, value: formatNumber(metrics.errorCount) },
                        { label: 'Успех, %', hint: METRICS_HINTS.successPercent, value: formatPercent(metrics.successPercent) },
                    ]}
                />
                <MetricsSection
                    title="Rate limit"
                    rows={[
                        { label: 'HTTP 429 (попытки)', hint: METRICS_HINTS.rateLimitCount, value: formatNumber(metrics.rateLimitCount) },
                        { label: 'HTTP-попытки', hint: METRICS_HINTS.httpAttemptCount, value: formatNumber(metrics.httpAttemptCount) },
                        { label: 'Запросов с 429', hint: METRICS_HINTS.requestsWith429Count, value: formatNumber(metrics.requestsWith429Count) },
                        { label: '429 от попыток, %', hint: METRICS_HINTS.rateLimitPercent, value: formatPercent(metrics.rateLimitPercent) },
                        { label: 'Запросов с 429, %', hint: METRICS_HINTS.requestsWith429Percent, value: formatPercent(metrics.requestsWith429Percent) },
                    ]}
                />
                <MetricsSection
                    title="Очередь и параллелизм"
                    rows={[
                        { label: 'Глубина очереди', hint: METRICS_HINTS.queueDepth, value: formatNumber(metrics.queueDepth) },
                        { label: 'Активные воркеры', hint: METRICS_HINTS.activeWorkers, value: formatNumber(metrics.activeWorkers) },
                        { label: 'Лимит параллелизма', hint: METRICS_HINTS.currentConcurrencyLimit, value: `${metrics.currentConcurrencyLimit} / ${metrics.maxConcurrencyLimit}` },
                        { label: 'Среднее ожидание, мс', hint: METRICS_HINTS.avgWaitTimeMs, value: formatNumber(Math.round(metrics.avgWaitTimeMs)) },
                    ]}
                />
                <MetricsSection
                    title="Отказы"
                    rows={[
                        { label: 'Отклонено очередью', hint: METRICS_HINTS.queueRejectedCount, value: formatNumber(metrics.queueRejectedCount) },
                        { label: 'Таймаут очереди', hint: METRICS_HINTS.queueTimeoutCount, value: formatNumber(metrics.queueTimeoutCount) },
                        { label: 'Исчерпан бюджет прокси', hint: METRICS_HINTS.proxyBudgetExhaustedCount, value: formatNumber(metrics.proxyBudgetExhaustedCount) },
                    ]}
                />
            </Box>

            {load != null && <InventoryLoadSourceTable data={load} />}
        </Box>
    );
}

export default ServiceMetricsPanel;
